import styled, { createGlobalStyle, css } from 'styled-components';
import Button from '../Button2.0';
import Link from '../Link/Link';

export const Root = styled.div`
	position: relative;
	font-weight: 500;
`;

export const Section = styled.section`
	width: 100%;
	padding: ${(props) => (props.noPadding ? '0' : '24px 0')};
	background-color: ${(props) => (props.isGray ? props.theme.grayColor : '#fff')};

	@media (max-width: 600px) {
		padding: 14px 0;
	}
`;

export const Container = styled.div`
	width: 100%;
	margin: 0;
	padding: 0 15px;
	box-sizing: border-box;

	@media (min-width: 1440px) {
		width: 1400px;
		margin: 0 auto;
		padding: 0;
	}
`;

const getGridTemplate = (withRelevance, withStat, withMortgage, withRelated, withDiscount, withReviews, screen) => {
	switch (screen) {
		case 'desktop':
			return css`'title price'
			'slider price'
			'props props'
			${withStat && `'descriptionStats descriptionStats'`}
			${withMortgage && `'mortgage mortgage'`}
			${withReviews && `'reviews reviews'`}
			'rating rating'
			'map map'
			${withDiscount && `'discount discount'`}
			${withRelevance && `'relevance relevance'`}
			${withRelated && `'related related'`}`;
		case 'tablet':
			return css`'title title'
			'slider slider'
			'price props'
			'price .'
			${withStat && `'descriptionStats descriptionStats'`}
			${withMortgage && `'mortgage mortgage'`}
			${withReviews && `'reviews reviews'`}
			'rating rating'
			'map map'
			${withDiscount && `'discount discount'`}
			${withRelevance && `'relevance relevance'`}
			${withRelated && `'related related'`}`;
		case 'mobile':
			return css`
			'slider'
			'title'
			'price'
			'props'
			${withStat && `'descriptionStats'`}
			${withMortgage && `'mortgage'`}
			${withReviews && `'reviews'`}
			'rating'
			'map'
			${withDiscount && `'discount'`}
			${withRelevance && `'relevance'`}
			${withRelated && `'related'`}`;
		default:
			return css`'title price'
			'slider price'
			'props price'
			${withStat && `'descriptionStats price'`}
			${withMortgage && `'mortgage price'`}
			${withReviews && `'reviews price'`}
			'rating price'
			'map price'
			${withDiscount && `'discount price'`}
			${withRelevance && `'relevance price'`}
			${withRelated && `'related price'`}`;
	}
};

export const MainGrid = styled.div`
	margin-top: 24px;
	position: relative;
	background-color: transparent;
	display: grid;
	grid-template-columns: 70% 1fr;
	grid-template-areas: ${({ $withRelevance, $withStat, $withMortgage, $withRelated, $withDiscount, $withReviews }) =>
		getGridTemplate($withRelevance, $withStat, $withMortgage, $withRelated, $withDiscount, $withReviews)};
	grid-gap: 16px;

	@media (max-width: 1025px) {
		grid-template-columns: 60% 1fr;
		grid-template-areas: ${({
			$withRelevance,
			$withStat,
			$withMortgage,
			$withRelated,
			$withDiscount,
			$withReviews,
		}) =>
			getGridTemplate(
				$withRelevance,
				$withStat,
				$withMortgage,
				$withRelated,
				$withDiscount,
				$withReviews,
				'desktop',
			)};
		grid-gap: 24px 20px;
	}

	@media (max-width: 769px) {
		grid-template-areas: ${({
			$withRelevance,
			$withStat,
			$withMortgage,
			$withRelated,
			$withDiscount,
			$withReviews,
		}) =>
			getGridTemplate(
				$withRelevance,
				$withStat,
				$withMortgage,
				$withRelated,
				$withDiscount,
				$withReviews,
				'tablet',
			)};
	}

	@media (max-width: 640px) {
		margin-top: 0;
		grid-gap: 20px;
		grid-template-columns: 100%;
		grid-template-areas: ${({
			$withRelevance,
			$withStat,
			$withMortgage,
			$withRelated,
			$withDiscount,
			$withReviews,
		}) =>
			getGridTemplate(
				$withRelevance,
				$withStat,
				$withMortgage,
				$withRelated,
				$withDiscount,
				$withReviews,
				'mobile',
			)};
	}
`;

export const Content = styled.div`
	display: grid;
	grid-area: content;
	grid-template-columns: 1fr;
	grid-template-areas:
		'slider'
		'props'
		'reviews'
		'rating'
		'map'
		'discount'
		'related';
`;

export const InfoCardsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	padding: 24px;
	background: #fff;
	border-radius: 8px;
	gap: 24px;
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);

	@media (max-width: 800px) {
		padding: 16px 14px;
		gap: 18px;
	}
`;

export const TitleWrapper = styled(InfoCardsWrapper)`
	grid-area: title;
	background-color: #fff;
	display: flex;
	flex-direction: column;

	@media (max-width: 800px) {
		gap: 14px;
	}
`;

export const DescriptionTitle = styled.h3`
	margin: 0 0 10px;
	${({ theme }) => theme.typography.textS};
`;

export const Title = styled.h1`
	${({ theme }) => theme.typography.textXL};
	margin: 0;
	hyphens: auto;
	hyphenate-limit-chars: 6 3 2;
	word-break: break-word;

	@media (max-width: 600px) {
		${({ theme }) => theme.typography.textBoldL};
	}
`;

export const Title2 = styled.h2`
	${({ theme }) => theme.typography.headlineXS};
	margin: 0 0 24px 0;

	a:hover {
		text-decoration: none;
	}
`;
export const TitleMax2 = styled.h2`
	${({ theme }) => theme.typography.textXL};
	margin: 0;

	@media (max-width: 600px) {
		${({ theme }) => theme.typography.textBoldL};
	}
`;

export const Title3 = styled.h3`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	font-size: 18px;
	font-weight: bold;
	line-height: 1.25;
	margin: 24px 0;

	.ratingMark {
		margin-left: 8px;
	}
`;

export const HeaderWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;

	@media (max-width: 1020px) {
		flex-direction: column;
	}
`;

export const AddressWrapper = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	align-items: flex-start;
	row-gap: 12px;
	color: ${({ theme }) => theme.primaryBlackColor};
	${({ theme }) => theme.typography.textS};
`;

export const AddressLine = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	row-gap: 4px;

	p {
		margin: 0;
	}
`;

export const AddressWithIcon = styled.div`
	display: block;

	p {
		margin: 0;
	}

	@media (min-width: 600px) {
		display: inline-flex;
	}
`;

export const LocationIcon = styled.svg`
	width: 16px;
	height: 16px;
	margin-right: 4px;
	flex-shrink: 0;
	fill: ${({ theme, isPrimary }) => (isPrimary ? theme.primaryColor : theme.darkGrayColor)};
`;

export const MetroItemWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;

	& > span {
		flex-wrap: nowrap;
		white-space: nowrap;
	}
`;

export const MetroLine = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	flex-wrap: wrap;
	width: 100%;
	gap: 5px 14px;
`;

export const MetroDistanceWrapper = styled.span`
	display: flex;
	margin-left: 6px;
	background-color: ${({ theme }) => theme.lightGrayColor};
	border-radius: 2px;
`;

export const MetroDistance = styled.span`
	display: flex;
	align-items: center;
	padding: 1px 3px 1px 0;
	font-size: 0.85em;
	white-space: nowrap;
	align-self: center;
`;

export const MetroText = styled.span`
	display: block;
	flex-shrink: 1;
	overflow: hidden;
	margin-left: 4px;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const BreadcrumbsWrapper = styled.div`
	display: flex;
`;

export const ReviewsWrapper = styled(InfoCardsWrapper)`
	grid-area: reviews;
`;

export const RatingsWrapper = styled(InfoCardsWrapper)`
	grid-area: rating;
`;

export const MapWrapper = styled(InfoCardsWrapper)`
	grid-area: map;
`;

export const DiscountWrapper = styled(InfoCardsWrapper)`
	grid-area: discount;
`;

export const MortgageWrapper = styled(InfoCardsWrapper)`
	grid-area: mortgage;
`;

export const RelatedWrapper = styled(InfoCardsWrapper)`
	grid-area: related;
`;

export const ContentWrapper = styled.div`
	display: flex;
	margin-top: 30px;

	@media (max-width: 1200px) {
		flex-direction: column;
	}
`;

export const ContentSliderWrapper = styled.div`
	width: 100%;
	grid-area: slider;
	display: flex;
	flex-direction: column;
	background-color: #fff;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);

	@media (max-width: 600px) {
		background-color: transparent;
	}
`;

export const PropsWrapper = styled(InfoCardsWrapper)`
	grid-area: ${({ $isBuilding }) => ($isBuilding ? `house` : `props`)};
`;

export const PropsList = styled.div`
	display: flex;
	align-items: start;
	flex: 1 1 100%;
	width: 100%;
	box-sizing: border-box;

	@media (max-width: 769px) {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
	}
`;

export const PropsItem = styled.div`
	display: flex;
	flex: 1 1 100%;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	padding: 10px;
	font-size: 14px;

	@media (max-width: 769px) {
		width: 100%;
		flex-direction: row;
		justify-content: space-between;
		padding: 0;
	}
`;

export const PropsTitle = styled.span`
	margin-bottom: 4px;
	color: ${({ theme }) => theme.darkGrayColor};
	${({ theme }) => theme.typography.textS};
`;

export const HousePropsList = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: 16px;
`;

export const HousePropsItem = styled.div`
	display: grid;
	grid-template-columns: minmax(160px, 0.5fr) 1fr;

	&:not(:last-child) {
		padding-bottom: 16px;
		border-bottom: 1px solid ${({ theme }) => theme.secondaryColor};
	}
`;

export const HousePropsTitle = styled.span`
	color: ${({ theme }) => theme.darkGrayColor};
	${({ theme }) => theme.typography.textS};
`;

export const HousePropsValue = styled.span`
	${({ theme }) => theme.typography.textMediumS};
`;

export const PropsTitleLink = styled.button`
	margin-bottom: 4px;
	padding: 0;
	background: transparent;
	box-shadow: none;
	border: none;
	color: ${({ theme }) => theme.primaryColor};
	text-decoration: underline;
	cursor: pointer;
	${({ theme }) => theme.typography.textMediumS};
`;

export const PropsValue = styled.span`
	text-align: left;
	${({ theme }) => theme.typography.textMediumM};

	@media (max-width: 769px) {
		text-align: right;
	}

	@media (max-width: 640px) {
		${({ theme }) => theme.typography.textMediumS};
	}
`;

export const PriceStats = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 100%;
	width: 100%;
	box-sizing: border-box;
	margin-top: 27px;

	@media (max-width: 640px) {
		margin-top: 0;
	}
`;

export const PriceStatsWrapper = styled.div`
	position: relative;
	display: grid;
	grid-column-gap: 4px;
	grid-template-columns: repeat(6, 1fr);
	align-items: end;
	height: 50px;
	flex-grow: 1;
	margin-bottom: 10px;
	padding-right: 90px;

	direction: rtl;
	box-sizing: border-box;
`;

export const PriceMonthInfo = styled.div`
	position: relative;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: end;

	p {
		position: absolute;
		top: 100%;
		left: 0;
		margin: 0;
		padding: 0;
		height: 10px;
		text-align: start;
		${({ theme }) => theme.typography.textXS};
		color: ${({ theme }) => theme.darkGrayColor};
		direction: ltr;
	}
`;

export const PriceStatsBar = styled.div`
	min-width: 35px;
	height: ${(props) => props.$height && `${props.$height}%`};
	background-color: ${({ theme, isCurrent }) => (isCurrent ? theme.primaryColor : theme.darkGrayColor)};

	&:hover {
		background-color: ${({ isCurrent, theme }) => (isCurrent ? theme.primaryColor : theme.middleGrayColor)};
	}

	@media (max-width: 1300px) {
		min-width: 25px;
	}

	@media (max-width: 321px) {
		min-width: 25px;
	}
`;

export const PriceStatsTooltip = styled.div`
	max-width: 130px;
	text-align: center;
`;

export const PriceStatsDiff = styled.p`
	margin: 0;
	color: #b8b8b8;
`;

export const PriceAverage = styled.div`
	position: absolute;
	left: 0;
	bottom: ${({ bottom }) => `${bottom}%`};
	width: calc(100% - 80px);
	color: #333;
	font-size: 12px;
	direction: ltr;
	border-top: 1px dashed #999;
`;

export const PriceAverageValue = styled.div`
	position: absolute;
	top: 20px;
	left: 100%;
	display: flex;
	flex-direction: column;
	padding: 2px 3px;
	transform: translateY(-50%);
	${({ theme }) => theme.typography.textMediumS};
	color: ${({ theme }) => theme.primaryBlackColor};

	span {
		${({ theme }) => theme.typography.textXS};
		color: ${({ theme }) => theme.darkGrayColor};
	}
`;

export const PriceAverageTooltip = styled.div`
	margin: 2px 0 14px;
	text-align: right;
	font-size: 12px;
	line-height: 1.33;
	color: #707070;

	span {
		font-size: 12px;
		color: ${({ theme }) => theme.primaryColor};
	}
`;

export const RatingWrapper = styled.div`
	display: flex;
	width: 100%;
	align-items: flex-start;

	@media (max-width: 1279px) {
		flex-wrap: wrap;
		justify-content: center;
	}
`;

export const RatingPieWrapper = styled.div`
	flex-basis: 100%;
	max-width: 450px;
	display: flex;
	align-items: center;
	justify-content: center;

	@media (min-width: 1280px) {
		flex-shrink: 0;
		flex-basis: 350px;
		max-width: unset;
	}
`;

export const RatingPropsWrapper = styled.div`
	flex: 1 1 100%;
`;

export const RatingList = styled.ul`
	display: block;
	column-count: 3;
	column-gap: 25px;
	padding: 0;

	@media (max-width: 980px) {
		column-count: 2;
	}

	@media (max-width: 480px) {
		column-count: 1;
	}
`;

export const RatingListItem = styled.li`
	display: flex;
	font-size: 14px;
	margin-bottom: 14px;
	-webkit-column-break-inside: avoid;
	page-break-inside: avoid;
	break-inside: avoid;
	overflow: visible;
`;

export const RatingIcon = styled.svg`
	margin-top: 1px;
	margin-right: 10px;
	flex-shrink: 0;
	width: 15px;
	height: 15px;
`;

export const RelatedItems = styled.div`
	display: grid;
	grid-auto-flow: column;
	overflow-x: auto;
	grid-gap: 15px;
	color: ${({ theme }) => theme.primaryBlackColor};
	margin: 0 -15px;
	padding: 0 15px 10px;
	width: max-content;
	max-width: 100%;
`;

export const RelatedApartsSlick = createGlobalStyle`
	.slick-related {
		position: relative;
		margin: 0;

		.slick-list {
			overflow: hidden;
		}

		.slick-track {
			display: flex;
			gap: 10px;
		}

		.slick-slide {
			display: block;
			box-sizing: border-box;
			vertical-align: top;

			& > div {
				height: 100%;
			}
		}

		.slick-arrow {
			position: absolute;
			top: 50%;
			width: 16px;
			height: 16px;
			background-color: transparent;
			border: none;
			padding: 0;
			outline: none;
			cursor: pointer;
			font-size: 0;
			transform: translateY(-50%);
			transition: opacity 0.3s ease;
			z-index: 99;

			&.slick-disabled {
				opacity: 0;
				pointer-events: none;
			}
		}

		.slick-prev {
			border-top: 1px solid #000;
			border-left: 1px solid #000;
			transform: rotate(-45deg);
			left: 0;
		}

		.slick-next {
			border-top: 1px solid #000;
			border-right: 1px solid #000;
			transform: rotate(45deg);
			right: 0;
		}
	}
`;

export const SliderWrapper = styled.div`
	position: relative;
	width: 100%;
	margin: 20px 0 0;
	padding-bottom: 50%;

	@media (max-width: 1025px) {
		padding-bottom: 70%;
	}
`;

export const SlideImg = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	z-index: 1;
`;

export const SlideOverflow = styled.div`
	position: absolute;
	top: 50%;
	left: 0;
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	font-size: 24px;
	font-weight: bold;
	line-height: 1.25;
	color: #fff;
	background-color: rgba(0, 0, 0, 0.5);
	transition: background-color 0.3s ease;
	transform: translateY(-50%);
	z-index: 2;

	svg {
		width: 50px;
		height: 50px;
		fill: #fff;
		margin-bottom: 24px;
	}
`;

export const Slide = styled(Button)`
	position: relative;
	display: block;
	width: 100%;
	height: 0;
	padding-bottom: 50%;
	background-color: #fff;
	border: none;
	box-shadow: none;
	cursor: pointer;
	box-sizing: border-box;

	&:hover {
		background-color: #fff;

		${SlideOverflow} {
			background-color: rgba(0, 0, 0, 0.25);
			color: ${(props) => props.theme.primaryColor};

			svg {
				fill: ${(props) => props.theme.primaryColor};
			}
		}
	}

	${SlideImg} {
		${({ isLayout }) =>
			isLayout &&
			`
			width: 95%;
			height: 95%;
			top: 2.5%;
			left: 2.5%;
			background-size: contain;
		`}
	}

	@media (max-width: 1025px) {
		padding-bottom: 70%;
	}
`;

export const ThumbImg = styled(SlideImg)`
	background-size: cover;
	background-position: center;
	border-radius: 5px;
`;

export const Thumb = styled.button`
	display: block;
	position: relative;
	width: 100%;
	height: 0;
	padding-bottom: 60%;
	cursor: pointer;
	box-sizing: border-box;
	background-color: #fff;
	border: ${({ isActive, theme }) => (isActive ? `2px solid ${theme.primaryColor}` : `2px solid #fff`)};
	border-radius: 5px;
	transition: opacity 0.2s;

	&::before {
		content: '';
		position: absolute;
		z-index: 2;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: block;
		background-color: ${(props) => props.isVideo && 'rgba(0, 0, 0, 0.5)'};
	}

	&::after {
		content: '';
		position: absolute;
		z-index: 3;
		top: 50%;
		left: 50%;
		width: 0;
		height: 0;
		transform: translate(-50%, -50%);
		border-style: ${({ isVideo }) => isVideo && 'solid'};
		border-width: ${({ isVideo }) => isVideo && '10px 0 10px 17.3px'};
		border-color: ${({ isVideo, isActive, theme }) =>
			isVideo &&
			(isActive
				? `transparent transparent transparent ${theme.yellowColor}`
				: 'transparent transparent transparent #ffffff')};
	}

	&:hover {
		opacity: 0.8;
	}

	${ThumbImg} {
		${({ isLayout }) =>
			isLayout &&
			`
			width: 90%;
			height: 90%;
			top: 5%;
			left: 5%;
			background-size: contain;
		`}
	}
`;

export const SlideStyle = createGlobalStyle`
	.apartMediaSlider {
		position: absolute;
		width: 100%;
		height: 100%;
		display: block;
		border-radius: 8px;

		.slick-list {
			grid-area: photo;
			overflow: hidden;
			height: 100%;
		}

		.slick-track {
			display: flex;
			align-items: center;
			height: 100%;
		}

		.slick-slide {
			display: block;
		}

		.slick-arrow {
			position: absolute;
			top: 50%;
			left: 24px;
			z-index: 1;
			width: 50px;
			height: 50px;
			border: none;
			border-radius: 50%;
			background-color: #fff;
			box-shadow: none;
			opacity: 0.45;
			font-size: 0;
			transform: translateY(-50%);
			cursor: pointer;

			@media (max-width: 640px) {
				left: 7px;
				width: 36px;
				height: 36px;
			}

			&::after {
				content: '';
				position: absolute;
				width: 14px;
				height: 14px;
				top: 50%;
				left: 50%;
				border: 2px solid #999;
				transform: translate(-50%, -50%) rotate(45deg);

				@media (max-width: 680px) {
					width: 8px;
					height: 8px;
				}
			}

			&:hover {
				&::after {
					border-color: #333;
				}
			}

			&.slick-disabled {
				opacity: 0;
				pointer-events: none;
			}
		}

		.slick-prev {
			&::after {
				border-top: none;
				border-right: none;
				margin-left: 3px;
			}
		}

		.slick-next {
			left: auto;
			right: 24px;

			&::after {
				border-left: none;
				border-bottom: none;
				margin-left: -3px;
			}

			@media (max-width: 640px) {
				right: 7px;
			}
		}

		&:hover .slick-arrow:not(.slick-disabled) {
			transition: opacity 0.3s ease;
			opacity: 1;
		}
	}

	.apartThumbsSlider {
		width: 100%;

		.slick-list {
			overflow: hidden;
			height: 100%;
		}

		.slick-track {
			height: 100%;
		}

		.slick-slide {
			display: inline-block;
			margin: 0;
			box-sizing: border-box;
		}
	}
`;

export const ArticleImg = styled.svg`
	grid-area: ArticleImg;
	width: 48px;
	height: 48px;
	fill: ${({ theme }) => theme.primaryColor};
	color: ${({ theme }) => theme.lightGrayColor};
`;
export const ArticleContainer = styled.div`
	grid-area: ArticleContainer;
	display: flex;
	flex-direction: column;
	justify-self: flex-start;
	align-items: flex-start;
	margin-left: 16px;
`;

export const ArticleTitle = styled(Link)`
	justify-self: left;
	grid-area: ArticleLink;
	padding: 5px 0;
	display: block;
	${({ theme }) => theme.typography.textMediumL};
	color: ${({ theme }) => theme.primaryBlackColor};
	text-decoration: none;
	white-space: nowrap;
	overflow: hidden;
	cursor: pointer;

	@media (max-width: 600px) {
		justify-self: center;
		${({ theme }) => theme.typography.textMediumM};
	}
`;

export const Article = styled.div`
	padding: 15px 0;
	display: grid;
	grid-template-columns: 50px 1.5fr 1fr;
	grid-template-areas: 'ArticleImg ArticleContainer ArticleBtn';
	align-items: center;
	justify-items: center;

	&:not(:last-child) {
		border-bottom: 1px solid #f0f1f4;
	}

	&:hover {
		${ArticleTitle} {
			color: ${(props) => props.theme.primaryColor};
			text-decoration: none;
		}
	}
`;

export const ArrowBtn = styled.button`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-right: 10px;
`;

export const ArrowIcon = styled.svg`
	width: 9px;
	height: 16px;
	fill: ${({ theme }) => theme.borderColor};

	@media (max-width: 600px) {
		margin-left: 10px;
	}
`;

export const ArticlesWrapper = styled.div`
	display: grid;
	grid-gap: 10px;
`;

export const GrayButton = styled(Button)`
	border-color: ${(props) => props.theme.grayColor};
	background-color: ${(props) => props.theme.grayColor};
	color: #000;
	cursor: pointer;

	&:hover {
		color: #000;
		background-color: ${(props) => props.theme.yellowColor};
		border-color: ${(props) => props.theme.yellowColor};
	}

	svg {
		margin-right: 6px;
		width: 14px;
		height: 14px;
	}
`;

export const YellowButton = styled(GrayButton)`
	background-color: ${(props) => props.theme.yellowColor};
	border-color: ${(props) => props.theme.yellowColor};

	&:hover {
		transition: opacity 0.3s ease;
		opacity: 0.7;
	}

	@media (max-width: 1245px) {
		margin: 20px 0;
	}
`;

export const Buttons = styled.div`
	position: relative;
	display: inline-block;
`;

export const Wrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex-wrap: wrap;
	margin: 20px 0;

	${Buttons} + ${Buttons} {
		margin-top: 10px;
	}
`;

export const MapRoot = styled.section`
	position: relative;
	width: 100%;
	height: ${(props) => (props.isFluid ? '100%' : '500px')};
	z-index: 1;

	@media (max-width: 768px) {
		display: flex;
		flex-direction: column-reverse;
		height: 30vh;
	}

	@media (max-width: 480px) {
		height: 50vh;
	}
`;

export const FormWrapper = styled.div`
	max-width: ${({ $fullWidth }) => ($fullWidth ? '100%' : '480px')};
	display: flex;
	flex-direction: column;

	@media (max-width: 600px) {
		height: 100vh;
		height: ${({ theme }) => theme.fullHeight};
		width: 100%;
	}
`;

export const ExtraLabel = styled.div`
	position: absolute;
	top: 0;
	left: 50%;
	padding: 8px 16px;
	font-size: 12px;
	line-height: 1.33;
	color: #333;
	border-radius: 16px;
	background-color: #fef9c9;
	transform: translate(-50%, -50%);
	white-space: nowrap;
`;

export const DescriptionStatsWrapper = styled(InfoCardsWrapper)`
	display: flex;
	flex-direction: column;
	grid-area: descriptionStats;
	gap: 24px;

	@media (max-width: 800px) {
		gap: 8px;
	}
`;

export const TitleWithLogo = styled.div`
	display: flex;
	gap: 10px 24px;
	align-items: center;
	flex-wrap: wrap;

	@media (max-width: 600px) {
		justify-content: space-between;
		gap: 0;
	}
`;

export const LoaderWrapper = styled.div`
	display: flex;
	flex: 1 1 100%;
	align-items: center;
	justify-content: center;
`;

export const BenefitsWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	grid-gap: 20px;
`;

export const RelatedTitleWrapper = styled.div`
	display: flex;
	flex: 1 1 100%;
	justify-content: space-between;
	align-items: center;

	@media (max-width: 600px) {
		gap: 10px;
		align-items: flex-start;
	}
`;
